import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Footer } from '../components/Footer';
export function PrivacyPage() {
  return <div className="min-h-screen bg-[#F8FAFC] flex flex-col font-sans text-[#0F172A]" data-id="element-850">
      {/* Simple Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-50" data-id="element-851">
        <div className="container mx-auto px-6 max-w-6xl h-20 flex items-center justify-between" data-id="element-852">
          <Link to="/" className="flex items-center gap-2 font-heading text-xl font-bold text-[#0F172A] no-underline" data-id="element-853">
            <img src="https://assets.corkship.io/CorkShip%20Logo%20Original%20png.png" alt="CorkShip Logo" className="w-8 h-8 object-contain" data-id="element-854" />
            CorkShip
          </Link>
          <Link to="/" className="flex items-center gap-2 text-sm font-medium text-[#64748B] hover:text-[#0284C7] transition-colors" data-id="element-855">
            <ArrowLeft size={16} data-id="element-856" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow py-16 px-6" data-id="element-857">
        <article className="max-w-3xl mx-auto bg-white rounded-2xl shadow-sm border border-slate-100 p-8 md:p-12" data-id="element-858">
          {/* Page Header */}
          <header className="mb-12 text-center md:text-left border-b border-slate-100 pb-8" data-id="element-859">
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-[#0F172A] mb-4" data-id="element-860">
              Privacy Policy
            </h1>
            <p className="text-[#64748B] text-lg" data-id="element-861">Last updated: January 2026</p>
          </header>

          {/* Content Body */}
          <div className="max-w-none" data-id="element-862">
            <section className="mb-12" data-id="element-863">
              <h2 className="font-heading font-bold text-2xl text-[#0F172A] mb-6" data-id="element-864">
                Overview
              </h2>
              <p className="text-[#0F172A] leading-[1.8] mb-6" data-id="element-865">
                CorkShip ("we", "our", or "us") is an application that helps
                Australian wineries manage their shipping and fulfilment through
                Australia Post's eParcel service. This Privacy Policy explains
                how we collect, use, and protect your information when you use
                our application.
              </p>
              <p className="text-[#0F172A] leading-[1.8] mb-6" data-id="element-866">
                We are committed to protecting your privacy and handling your
                data transparently. We do not sell your data to third parties.
              </p>
            </section>

            <hr className="my-12 border-slate-200" data-id="element-867" />

            <section className="mb-12" data-id="element-868">
              <h2 className="font-heading font-bold text-2xl text-[#0F172A] mb-8" data-id="element-869">
                Information We Collect
              </h2>

              <h3 className="font-heading font-bold text-lg text-[#0F172A] mt-8 mb-4" data-id="element-870">
                From Your Platform (when you install the app)
              </h3>
              <p className="text-[#0F172A] leading-[1.8] mb-4" data-id="element-871">
                When you install CorkShip on your store, we access the following
                data through your platform's API:
              </p>
              <ul className="list-disc pl-6 space-y-3 mb-6 text-[#0F172A] leading-[1.8]" data-id="element-872">
                <li data-id="element-873">
                  <strong data-id="element-874">Store information:</strong> Shop name, domain, email
                  address, currency, and timezone
                </li>
                <li data-id="element-875">
                  <strong data-id="element-876">Order information:</strong> Order details, customer
                  shipping addresses, product information, and order status
                </li>
                <li data-id="element-877">
                  <strong data-id="element-878">Product information:</strong> Product names, variants,
                  and attributes (to calculate bottle counts)
                </li>
                <li data-id="element-879">
                  <strong data-id="element-880">Customer information:</strong> Names and shipping
                  addresses (for generating shipping labels)
                </li>
              </ul>
              <p className="text-[#0F172A] leading-[1.8] mb-6" data-id="element-881">
                We only access order and customer data for orders you choose to
                process through CorkShip.
              </p>

              <h3 className="font-heading font-bold text-lg text-[#0F172A] mt-8 mb-4" data-id="element-882">
                From You (when you configure the app)
              </h3>
              <ul className="list-disc pl-6 space-y-3 mb-6 text-[#0F172A] leading-[1.8]" data-id="element-883">
                <li data-id="element-884">
                  <strong data-id="element-885">Australia Post credentials:</strong> Your eParcel API
                  credentials (Account Number, API Key, API Secret)
                </li>
                <li data-id="element-886">
                  <strong data-id="element-887">Shipping preferences:</strong> Parcel size
                  configurations, default weights, and service preferences
                </li>
                <li data-id="element-888">
                  <strong data-id="element-889">Postcode rules:</strong> Any allowlist or blocklist
                  rules you configure for shipping validation
                </li>
              </ul>

              <h3 className="font-heading font-bold text-lg text-[#0F172A] mt-8 mb-4" data-id="element-890">
                Automatically Collected
              </h3>
              <ul className="list-disc pl-6 space-y-3 mb-6 text-[#0F172A] leading-[1.8]" data-id="element-891">
                <li data-id="element-892">
                  <strong data-id="element-893">Usage data:</strong> Which features you use and how
                  often (for improving the app)
                </li>
                <li data-id="element-894">
                  <strong data-id="element-895">Error logs:</strong> Technical errors that occur (for
                  troubleshooting and fixing bugs)
                </li>
              </ul>
            </section>

            <hr className="my-12 border-slate-200" data-id="element-896" />

            <section className="mb-12" data-id="element-897">
              <h2 className="font-heading font-bold text-2xl text-[#0F172A] mb-6" data-id="element-898">
                How We Use Your Information
              </h2>
              <p className="text-[#0F172A] leading-[1.8] mb-4" data-id="element-899">
                We use your information solely to provide the CorkShip service:
              </p>
              <ul className="list-disc pl-6 space-y-3 mb-6 text-[#0F172A] leading-[1.8]" data-id="element-900">
                <li data-id="element-901">
                  <strong data-id="element-902">Processing shipments:</strong> Creating shipping
                  labels and manifests through Australia Post
                </li>
                <li data-id="element-903">
                  <strong data-id="element-904">Syncing data:</strong> Updating your orders with
                  tracking numbers and fulfilment status
                </li>
                <li data-id="element-905">
                  <strong data-id="element-906">Validating addresses:</strong> Checking shipping
                  addresses against Australia Post's address database
                </li>
                <li data-id="element-907">
                  <strong data-id="element-908">Checkout validation:</strong> Validating postcodes at
                  checkout (if enabled)
                </li>
                <li data-id="element-909">
                  <strong data-id="element-910">Improving the service:</strong> Understanding how the
                  app is used to make it better
                </li>
              </ul>

              <p className="text-[#0F172A] leading-[1.8] mb-4" data-id="element-911">We do not:</p>
              <ul className="list-disc pl-6 space-y-3 mb-6 text-[#0F172A] leading-[1.8]" data-id="element-912">
                <li data-id="element-913">Sell your data to third parties</li>
                <li data-id="element-914">Use your data for advertising</li>
                <li data-id="element-915">Share your customer lists with anyone</li>
                <li data-id="element-916">
                  Access data from orders you don't process through CorkShip
                </li>
              </ul>
            </section>

            <hr className="my-12 border-slate-200" data-id="element-917" />

            <section className="mb-12" data-id="element-918">
              <h2 className="font-heading font-bold text-2xl text-[#0F172A] mb-8" data-id="element-919">
                How We Protect Your Information
              </h2>

              <h3 className="font-heading font-bold text-lg text-[#0F172A] mt-8 mb-4" data-id="element-920">
                Encryption
              </h3>
              <ul className="list-disc pl-6 space-y-3 mb-6 text-[#0F172A] leading-[1.8]" data-id="element-921">
                <li data-id="element-922">
                  All Australia Post credentials are encrypted using AES-256-GCM
                  encryption before storage
                </li>
                <li data-id="element-923">Data is transmitted over HTTPS/TLS</li>
                <li data-id="element-924">Platform access tokens are stored encrypted</li>
              </ul>

              <h3 className="font-heading font-bold text-lg text-[#0F172A] mt-8 mb-4" data-id="element-925">
                Data Isolation
              </h3>
              <ul className="list-disc pl-6 space-y-3 mb-6 text-[#0F172A] leading-[1.8]" data-id="element-926">
                <li data-id="element-927">
                  Each store's data is completely isolated using database
                  row-level security
                </li>
                <li data-id="element-928">
                  You cannot access another store's data, and they cannot access
                  yours
                </li>
              </ul>

              <h3 className="font-heading font-bold text-lg text-[#0F172A] mt-8 mb-4" data-id="element-929">
                Infrastructure
              </h3>
              <ul className="list-disc pl-6 space-y-3 mb-6 text-[#0F172A] leading-[1.8]" data-id="element-930">
                <li data-id="element-931">
                  Our application is hosted on Railway (railway.app) with
                  encrypted databases
                </li>
                <li data-id="element-932">Database hosted on PostgreSQL with encryption at rest</li>
                <li data-id="element-933">Regular automated backups</li>
              </ul>
            </section>

            <hr className="my-12 border-slate-200" data-id="element-934" />

            <section className="mb-12" data-id="element-935">
              <h2 className="font-heading font-bold text-2xl text-[#0F172A] mb-6" data-id="element-936">
                Data Retention
              </h2>
              <ul className="list-disc pl-6 space-y-3 mb-6 text-[#0F172A] leading-[1.8]" data-id="element-937">
                <li data-id="element-938">
                  <strong data-id="element-939">Active accounts:</strong> We retain your data for as
                  long as you have CorkShip installed
                </li>
                <li data-id="element-940">
                  <strong data-id="element-941">After uninstallation:</strong> We delete your data
                  within 30 days of app uninstallation
                </li>
                <li data-id="element-942">
                  <strong data-id="element-943">Order data:</strong> Historical shipment records are
                  kept for your reference while the app is installed
                </li>
              </ul>
            </section>

            <hr className="my-12 border-slate-200" data-id="element-944" />

            <section className="mb-12" data-id="element-945">
              <h2 className="font-heading font-bold text-2xl text-[#0F172A] mb-8" data-id="element-946">
                Your Rights
              </h2>

              <h3 className="font-heading font-bold text-lg text-[#0F172A] mt-8 mb-4" data-id="element-947">
                Access and Export
              </h3>
              <p className="text-[#0F172A] leading-[1.8] mb-6" data-id="element-948">
                You can request a copy of all data we hold about your store by
                contacting us at{' '}
                <a href="mailto:privacy@corkship.com.au" className="text-[#0284C7] hover:underline" data-id="element-949">
                  privacy@corkship.com.au
                </a>
                . We will provide this within 30 days.
              </p>

              <h3 className="font-heading font-bold text-lg text-[#0F172A] mt-8 mb-4" data-id="element-950">
                Deletion
              </h3>
              <p className="text-[#0F172A] leading-[1.8] mb-4" data-id="element-951">
                You can request deletion of your data at any time by:
              </p>
              <ul className="list-disc pl-6 space-y-3 mb-6 text-[#0F172A] leading-[1.8]" data-id="element-952">
                <li data-id="element-953">
                  Uninstalling the app from your store (data deleted within 30
                  days), or
                </li>
                <li data-id="element-954">
                  Contacting us at{' '}
                  <a href="mailto:privacy@corkship.com.au" className="text-[#0284C7] hover:underline" data-id="element-955">
                    privacy@corkship.com.au
                  </a>{' '}
                  for immediate deletion
                </li>
              </ul>

              <h3 className="font-heading font-bold text-lg text-[#0F172A] mt-8 mb-4" data-id="element-956">
                GDPR Compliance
              </h3>
              <p className="text-[#0F172A] leading-[1.8] mb-4" data-id="element-957">
                If you or your customers are in the European Economic Area, you
                have additional rights under GDPR:
              </p>
              <ul className="list-disc pl-6 space-y-3 mb-6 text-[#0F172A] leading-[1.8]" data-id="element-958">
                <li data-id="element-959">Right to access</li>
                <li data-id="element-960">Right to rectification</li>
                <li data-id="element-961">Right to erasure</li>
                <li data-id="element-962">Right to data portability</li>
              </ul>
              <p className="text-[#0F172A] leading-[1.8] mb-6" data-id="element-963">
                We respond to mandatory GDPR webhooks for customer data requests
                and shop data erasure requests.
              </p>
            </section>

            <hr className="my-12 border-slate-200" data-id="element-964" />

            <section className="mb-12" data-id="element-965">
              <h2 className="font-heading font-bold text-2xl text-[#0F172A] mb-6" data-id="element-966">
                Third-Party Services
              </h2>
              <p className="text-[#0F172A] leading-[1.8] mb-6" data-id="element-967">
                CorkShip integrates with the following third-party services to
                provide our functionality:
              </p>

              <div className="space-y-6 mb-6" data-id="element-968">
                <div data-id="element-969">
                  <h4 className="font-bold text-lg text-[#0F172A] mb-2" data-id="element-970">
                    Shopify / Commerce7
                  </h4>
                  <p className="text-[#0F172A] leading-[1.8] mb-1" data-id="element-971">
                    Purpose: App platform, order and customer data
                  </p>
                  <p className="text-[#0F172A] leading-[1.8]" data-id="element-972">
                    Privacy Policy:{' '}
                    <a href="https://www.shopify.com/legal/privacy" target="_blank" rel="noopener noreferrer" className="text-[#0284C7] hover:underline" data-id="element-973">
                      shopify.com/legal/privacy
                    </a>
                    {' | '}
                    <a href="https://commerce7.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#0284C7] hover:underline" data-id="element-974">
                      commerce7.com/privacy
                    </a>
                  </p>
                </div>

                <div data-id="element-975">
                  <h4 className="font-bold text-lg text-[#0F172A] mb-2" data-id="element-976">
                    Australia Post
                  </h4>
                  <p className="text-[#0F172A] leading-[1.8] mb-1" data-id="element-977">
                    Purpose: Shipping label generation, address validation,
                    tracking
                  </p>
                  <p className="text-[#0F172A] leading-[1.8]" data-id="element-978">
                    Privacy Policy:{' '}
                    <a href="https://auspost.com.au/privacy" target="_blank" rel="noopener noreferrer" className="text-[#0284C7] hover:underline" data-id="element-979">
                      https://auspost.com.au/privacy
                    </a>
                  </p>
                </div>

                <div data-id="element-980">
                  <h4 className="font-bold text-lg text-[#0F172A] mb-2" data-id="element-981">
                    Railway (Hosting)
                  </h4>
                  <p className="text-[#0F172A] leading-[1.8] mb-1" data-id="element-982">
                    Purpose: Application and database hosting
                  </p>
                  <p className="text-[#0F172A] leading-[1.8]" data-id="element-983">
                    Privacy Policy:{' '}
                    <a href="https://railway.app/legal/privacy" target="_blank" rel="noopener noreferrer" className="text-[#0284C7] hover:underline" data-id="element-984">
                      https://railway.app/legal/privacy
                    </a>
                  </p>
                </div>
              </div>

              <p className="text-[#0F172A] leading-[1.8]" data-id="element-985">
                We do not share your data with any other third parties.
              </p>
            </section>

            <hr className="my-12 border-slate-200" data-id="element-986" />

            <section className="mb-12" data-id="element-987">
              <h2 className="font-heading font-bold text-2xl text-[#0F172A] mb-6" data-id="element-988">
                Cookies
              </h2>
              <p className="text-[#0F172A] leading-[1.8] mb-4" data-id="element-989">
                CorkShip uses essential cookies only:
              </p>
              <ul className="list-disc pl-6 space-y-3 mb-6 text-[#0F172A] leading-[1.8]" data-id="element-990">
                <li data-id="element-991">
                  <strong data-id="element-992">Session cookies:</strong> To keep you logged in while
                  using the app within your store admin
                </li>
                <li data-id="element-993">
                  <strong data-id="element-994">Security cookies:</strong> To protect against
                  cross-site request forgery
                </li>
              </ul>
              <p className="text-[#0F172A] leading-[1.8]" data-id="element-995">
                We do not use tracking cookies or analytics cookies.
              </p>
            </section>

            <hr className="my-12 border-slate-200" data-id="element-996" />

            <section className="mb-12" data-id="element-997">
              <h2 className="font-heading font-bold text-2xl text-[#0F172A] mb-6" data-id="element-998">
                Changes to This Policy
              </h2>
              <p className="text-[#0F172A] leading-[1.8] mb-4" data-id="element-999">
                We may update this Privacy Policy from time to time. We will
                notify you of any significant changes by:
              </p>
              <ul className="list-disc pl-6 space-y-3 mb-6 text-[#0F172A] leading-[1.8]" data-id="element-1000">
                <li data-id="element-1001">Posting a notice in the app</li>
                <li data-id="element-1002">
                  Updating the "Last updated" date at the top of this policy
                </li>
              </ul>
              <p className="text-[#0F172A] leading-[1.8]" data-id="element-1003">
                Continued use of CorkShip after changes constitutes acceptance
                of the updated policy.
              </p>
            </section>

            <hr className="my-12 border-slate-200" data-id="element-1004" />

            <section className="mb-12" data-id="element-1005">
              <h2 className="font-heading font-bold text-2xl text-[#0F172A] mb-6" data-id="element-1006">
                Contact Us
              </h2>
              <p className="text-[#0F172A] leading-[1.8] mb-4" data-id="element-1007">
                If you have any questions about this Privacy Policy or how we
                handle your data:
              </p>
              <ul className="list-none space-y-2 mb-6 text-[#0F172A] leading-[1.8]" data-id="element-1008">
                <li data-id="element-1009">
                  <strong data-id="element-1010">Email:</strong>{' '}
                  <a href="mailto:privacy@corkship.com.au" className="text-[#0284C7] hover:underline" data-id="element-1011">
                    privacy@corkship.com.au
                  </a>
                </li>
                <li data-id="element-1012">
                  <strong data-id="element-1013">Address:</strong> CorkShip Melbourne, Victoria
                  Australia
                </li>
              </ul>
            </section>

            <hr className="my-12 border-slate-200" data-id="element-1014" />

            <section data-id="element-1015">
              <h2 className="font-heading font-bold text-2xl text-[#0F172A] mb-6" data-id="element-1016">
                Summary
              </h2>
              <div className="overflow-x-auto" data-id="element-1017">
                <table className="w-full text-left border-collapse mt-6" data-id="element-1018">
                  <thead data-id="element-1019">
                    <tr className="bg-slate-50 border-b border-slate-200" data-id="element-1020">
                      <th className="p-4 font-bold text-[#0F172A]" data-id="element-1021">
                        What we collect
                      </th>
                      <th className="p-4 font-bold text-[#0F172A]" data-id="element-1022">Why</th>
                      <th className="p-4 font-bold text-[#0F172A]" data-id="element-1023">
                        Shared with
                      </th>
                    </tr>
                  </thead>
                  <tbody className="text-sm" data-id="element-1024">
                    <tr className="border-b border-slate-100" data-id="element-1025">
                      <td className="p-4" data-id="element-1026">Store info</td>
                      <td className="p-4" data-id="element-1027">To identify your account</td>
                      <td className="p-4" data-id="element-1028">No one</td>
                    </tr>
                    <tr className="border-b border-slate-100" data-id="element-1029">
                      <td className="p-4" data-id="element-1030">Order & customer data</td>
                      <td className="p-4" data-id="element-1031">To create shipping labels</td>
                      <td className="p-4" data-id="element-1032">Australia Post (for shipping)</td>
                    </tr>
                    <tr className="border-b border-slate-100" data-id="element-1033">
                      <td className="p-4" data-id="element-1034">Australia Post credentials</td>
                      <td className="p-4" data-id="element-1035">
                        To connect to your eParcel account
                      </td>
                      <td className="p-4" data-id="element-1036">Australia Post (authentication)</td>
                    </tr>
                    <tr className="border-b border-slate-100" data-id="element-1037">
                      <td className="p-4" data-id="element-1038">Shipping preferences</td>
                      <td className="p-4" data-id="element-1039">To configure your parcel sizes</td>
                      <td className="p-4" data-id="element-1040">No one</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="mt-8 font-bold text-center text-lg text-[#0F172A]" data-id="element-1041">
                We do not sell your data. Ever.
              </p>
            </section>
          </div>
        </article>
      </main>

      {/* Footer */}
      <Footer data-id="element-1042" />
    </div>;
}